import { NormalizedNumber } from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'
import { useEffect, useState } from 'react'

import { createCollateralObjectives } from './createCollateralObjectives'
import { getUpdatedUserSummary } from './getUpdatedUserSummary'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { Token } from '@/domain/types/Token'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/features/sparklend/common/utils/liquidation-risk-warning/useLiquidationRiskWarning'
import {
  getValidateSetUseAsCollateralArgs,
  SetUseAsCollateralValidationIssue,
  validateSetUseAsCollateral,
} from '@/features/sparklend/common/validation/validateSetUseAsCollateral'

export interface UseCollateralDialogParams {
  useAsCollateral: boolean
  token: Token
  chainId: number
}

export interface UseCollateralDialogResult {
  collateral: Token
  collateralBalance: NormalizedNumber
  validationIssue: SetUseAsCollateralValidationIssue | undefined
  pageStatus: PageStatus
  currentHealthFactor?: BigNumber
  updatedHealthFactor?: BigNumber
  riskAcknowledgement: RiskAcknowledgementInfo
  actionsViewState: ActionsViewState
}

export function useCollateralDialog({
  useAsCollateral,
  token,
  chainId,
}: UseCollateralDialogParams): UseCollateralDialogResult {
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const marketToken = marketInfo.findOneReserveBySymbol(token.symbol).token
  const collateralPosition = marketInfo.findOnePositionByToken(marketToken)
  const currentHealthFactor = marketInfo.userPositionSummary.healthFactor
  const collateralBalance = collateralPosition.collateralBalance

  const { healthFactor: updatedHealthFactor } = getUpdatedUserSummary({
    useAsCollateral,
    token: marketToken,
    marketInfo,
    aaveData,
    nativeAssetInfo,
  })
  const objectives = createCollateralObjectives(marketToken, useAsCollateral)

  const validationIssue = validateSetUseAsCollateral(
    getValidateSetUseAsCollateralArgs({
      useAsCollateral,
      collateral: marketToken,
      healthFactorAfterWithdrawal: updatedHealthFactor,
      marketInfo,
    }),
  )

  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-set-collateral',
    isFormValid: !validationIssue,
    currentHealthFactor,
    updatedHealthFactor,
  })

  const actionsEnabled = !validationIssue && !disableActionsByRisk

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  useEffect(() => {
    resetActions()
  }, [resetActions])

  return {
    collateral: marketToken,
    collateralBalance,
    validationIssue,
    currentHealthFactor,
    updatedHealthFactor,
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    riskAcknowledgement,
    actionsViewState,
  }
}
